import { useRef } from "react";
import { X, Printer, Plane, Users, Clock, CheckCircle, AlertTriangle, Shield, Rocket } from "lucide-react";

const FDP_META = {
  normal:    { label: "NORMAL",    color: "#34d399", bg: "rgba(52,211,153,0.1)"  },
  watch:     { label: "WATCH",     color: "#f59e0b", bg: "rgba(245,158,11,0.1)" },
  violation: { label: "VIOLATION", color: "#f87171", bg: "rgba(248,113,113,0.1)"},
};

function FatigueBar({ score }) {
  const pct = Math.min(100, score || 0);
  const col = pct < 40 ? "#34d399" : pct < 65 ? "#f59e0b" : "#f87171";
  return (
    <div className="flex items-center gap-1.5">
      <div className="h-1.5 w-16 bg-white/10 rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: col }} />
      </div>
      <span className="text-[9px] font-mono font-bold" style={{ color: col }}>{score ?? "—"}</span>
    </div>
  );
}

function SectorRow({ sector, phase, index }) {
  const isPhase2 = phase === 2;
  const accent   = isPhase2 ? "#818cf8" : "#f59e0b";
  const bg       = isPhase2 ? "rgba(129,140,248,0.08)" : "rgba(245,158,11,0.08)";
  const border   = isPhase2 ? "rgba(129,140,248,0.25)" : "rgba(245,158,11,0.25)";
  return (
    <div className="flex items-center gap-3 px-3 py-2 rounded-sm" style={{ background: bg, border: `1px solid ${border}` }}>
      <span className="text-[9px] font-mono font-bold w-6 text-center" style={{ color: accent }}>
        {isPhase2 ? `E${index + 1}` : `S${index + 1}`}
      </span>
      <Plane size={9} style={{ color: accent, flexShrink: 0 }} />
      <span className="text-xs font-mono font-bold text-foreground">{sector.dep} → {sector.arr}</span>
      <span className="text-[10px] font-mono text-muted-foreground">{sector.depTime} – {sector.arrTime}</span>
      <span className="ml-auto text-[10px] font-mono" style={{ color: accent }}>{sector.blockH?.toFixed(1)}h block</span>
    </div>
  );
}

function CrewRow({ person, role }) {
  if (!person?.name) return null;
  const high = (person.fatigue_score || 0) > 65;
  return (
    <div className={`flex items-center gap-3 px-3 py-2 rounded-sm border ${high ? "bg-amber-500/8 border-amber-500/20" : "bg-white/[0.03] border-border/20"}`}>
      <Users size={11} className={high ? "text-amber-400" : "text-teal-400"} />
      <div className="flex-1">
        <div className="text-xs font-bold text-foreground">{person.name}</div>
        <div className="text-[9px] text-muted-foreground font-mono">{person.employee_id} · {role}</div>
      </div>
      <FatigueBar score={person.fatigue_score} />
      {person.fdp_contribution && (
        <div className="text-[9px] font-mono text-muted-foreground">FDP: {person.fdp_contribution?.toFixed(1)}h</div>
      )}
      {high && <AlertTriangle size={9} className="text-amber-400" />}
    </div>
  );
}

export default function ManagementSignoff({ submission, onClose }) {
  const printRef = useRef(null);

  if (!submission) return null;

  const sch         = submission.schedule || {};
  const ca          = submission.crew_assignments || {};
  const isDualCrew  = ca.dual_crew || submission.requires_dual_crew || false;
  const fdp         = submission.fdp_analysis || {};
  const fdpPhase1   = submission.fdp_phase1 || {};
  const fdpPhase2   = submission.fdp_phase2 || {};
  const fdpMeta     = FDP_META[fdp.status || "normal"];
  const phase1      = sch.scheduled_sectors || [];
  const phase2      = sch.extra_legs || [];
  const totalSectors = phase1.length + phase2.length;
  const totalBH = (() => {
    const stored = (sch.total_scheduled_bh || 0) + (sch.total_extra_bh || 0);
    if (stored > 0) return stored;
    return [...phase1, ...phase2].reduce((s, x) => s + (x.blockH || 0), 0);
  })();
  const approval    = submission.approval || {};
  const deploy      = submission.deployment || {};

  const handlePrint = () => window.print();

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
      data-testid="management-signoff-modal">
      <div ref={printRef} className="bg-[#0f1117] border border-border rounded-sm w-full max-w-3xl flex flex-col"
        style={{ maxHeight: "92vh" }}>

        {/* Modal header — hidden on print */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border/50 flex-shrink-0 print-hide">
          <div className="flex items-center gap-3">
            <Shield size={16} className="text-indigo-400" />
            <div>
              <div className="font-black text-sm uppercase tracking-widest text-foreground">Management Sign-off</div>
              <div className="text-[10px] text-muted-foreground">{submission.scenario_name}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handlePrint}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold rounded-sm bg-white/5 border border-border text-muted-foreground hover:text-foreground transition-all"
              data-testid="print-signoff-btn">
              <Printer size={10} /> Print
            </button>
            <button onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground transition-colors">
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">

          {/* ── Title block ── */}
          <div className="border border-border/40 rounded-sm bg-white/[0.02] px-5 py-4">
            <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground mb-2">AKASA AIR · CREW OPERATIONS</div>
            <h1 className="text-lg font-black uppercase tracking-widest text-foreground mb-0.5">
              Operational Schedule Sign-off
            </h1>
            <div className="text-[11px] text-muted-foreground">
              {submission.scenario_name} · {submission.base} Base · {submission.tail_number}
            </div>
          </div>

          {/* ── Status timeline ── */}
          <div>
            <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground mb-3">Approval Chain</div>
            <div className="flex items-start gap-0">
              {[
                {
                  icon: CheckCircle, label: "Submitted",
                  sub: submission.submitted_by || "Crew Scheduler",
                  time: new Date(submission.submitted_at).toLocaleDateString("en-IN", {day:"2-digit", month:"short", year:"numeric"}),
                  color: "text-amber-400", done: true,
                },
                {
                  icon: Users, label: "AI Crew Selection",
                  sub: `Score: ${ca.crew_efficiency_score || 82}/100`,
                  time: "Auto-assigned",
                  color: "text-indigo-400", done: true,
                },
                {
                  icon: CheckCircle, label: approval.status === "approved" ? "Approved" : (approval.status === "rejected" ? "Rejected" : "Pending"),
                  sub: approval.approved_by || "Awaiting",
                  time: approval.approved_at ? new Date(approval.approved_at).toLocaleDateString("en-IN", {day:"2-digit", month:"short"}) : "—",
                  color: approval.status === "approved" ? "text-teal-400" : (approval.status === "rejected" ? "text-red-400" : "text-muted-foreground"),
                  done: !!approval.approved_at,
                },
                {
                  icon: Rocket, label: deploy.effective_date ? "Deployed" : "Deployment",
                  sub: deploy.deployed_by || "Pending",
                  time: deploy.effective_date || "—",
                  color: deploy.effective_date ? "text-emerald-400" : "text-muted-foreground",
                  done: !!deploy.effective_date,
                },
              ].map(({ icon: Icon, label, sub, time, color, done }, i, arr) => (
                <div key={i} className="flex-1 flex flex-col items-center relative">
                  {i < arr.length - 1 && (
                    <div className={`absolute top-3.5 left-1/2 w-full h-px ${done ? "bg-border/50" : "bg-border/20"}`} style={{ transform: "translateX(50%)" }} />
                  )}
                  <div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center mb-2 z-10 ${done ? "border-current bg-current/10" : "border-border/30 bg-background"} ${color}`}>
                    <Icon size={12} />
                  </div>
                  <div className={`text-[9px] font-bold text-center ${color}`}>{label}</div>
                  <div className="text-[8px] text-muted-foreground text-center">{sub}</div>
                  <div className="text-[8px] font-mono text-muted-foreground/60 text-center">{time}</div>
                </div>
              ))}
            </div>
          </div>

          {/* ── FDP + Schedule summary ── */}
          {isDualCrew ? (
            <div className="space-y-3">
              {/* Dual crew violation banner */}
              <div className="flex items-start gap-2 px-4 py-3 rounded-sm border border-red-500/30 bg-red-500/8">
                <AlertTriangle size={14} className="text-red-400 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-xs font-bold text-red-300 mb-0.5">DGCA FDP Violation — Dual Crew Operation</div>
                  <div className="text-[10px] text-red-300/70">
                    {ca.dual_crew_reason || `Combined FDP ${fdp.fdp_hours?.toFixed(1)}h exceeds DGCA ${fdp.dgca_limit}h limit. Two separate crew teams assigned per DGCA FDTL regulations.`}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                {/* Combined FDP overview */}
                <div className="border border-border/30 rounded-sm bg-white/[0.02] p-3">
                  <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Combined FDP</div>
                  {[
                    ["Report",  fdp.report_time  || "—", "text-foreground"],
                    ["Release", fdp.release_time || "—", "text-foreground"],
                    ["Total",   `${fdp.fdp_hours?.toFixed(1) || 0}h`, "text-red-400"],
                    ["Limit",   `${fdp.dgca_limit || 13}h`,          "text-muted-foreground"],
                  ].map(([l,v,c]) => (
                    <div key={l} className="flex justify-between text-[10px] mb-1">
                      <span className="text-muted-foreground">{l}</span>
                      <span className={`font-mono font-bold ${c}`}>{v}</span>
                    </div>
                  ))}
                  <div className="flex justify-between items-center pt-1 border-t border-border/20">
                    <span className="text-[9px] text-muted-foreground">Status</span>
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm font-mono"
                      style={{ color: fdpMeta.color, background: fdpMeta.bg }}>{fdpMeta.label}</span>
                  </div>
                </div>
                {/* Phase 1 FDP */}
                <div className="border border-amber-500/25 rounded-sm bg-amber-500/5 p-3">
                  <div className="text-[9px] font-bold uppercase tracking-widest text-amber-400 mb-2">Crew 1 — Phase 1 FDP</div>
                  {[
                    ["Report",  fdpPhase1.report_time  || "—", "text-foreground"],
                    ["Release", fdpPhase1.release_time || "—", "text-foreground"],
                    ["Total",   `${fdpPhase1.fdp_hours?.toFixed(1) || 0}h`, "text-amber-300"],
                    ["Status",  (fdpPhase1.status || "normal").toUpperCase(),
                      fdpPhase1.status === "violation" ? "text-red-400" : fdpPhase1.status === "watch" ? "text-amber-400" : "text-emerald-400"],
                  ].map(([l,v,c]) => (
                    <div key={l} className="flex justify-between text-[10px] mb-1">
                      <span className="text-muted-foreground">{l}</span>
                      <span className={`font-mono font-bold ${c}`}>{v}</span>
                    </div>
                  ))}
                </div>
                {/* Phase 2 FDP */}
                <div className="border border-indigo-500/25 rounded-sm bg-indigo-500/5 p-3">
                  <div className="text-[9px] font-bold uppercase tracking-widest text-indigo-400 mb-2">Crew 2 — Phase 2 FDP</div>
                  {[
                    ["Report",  fdpPhase2.report_time  || "—", "text-foreground"],
                    ["Release", fdpPhase2.release_time || "—", "text-foreground"],
                    ["Total",   `${fdpPhase2.fdp_hours?.toFixed(1) || 0}h`, "text-indigo-300"],
                    ["Status",  (fdpPhase2.status || "normal").toUpperCase(),
                      fdpPhase2.status === "violation" ? "text-red-400" : fdpPhase2.status === "watch" ? "text-amber-400" : "text-emerald-400"],
                  ].map(([l,v,c]) => (
                    <div key={l} className="flex justify-between text-[10px] mb-1">
                      <span className="text-muted-foreground">{l}</span>
                      <span className={`font-mono font-bold ${c}`}>{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <div className="border border-border/30 rounded-sm bg-white/[0.02] p-4">
                <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground mb-3">Flight Duty Period</div>
                <div className="space-y-2">
                  {[
                    ["Report Time",    fdp.report_time  || "—",             "text-foreground"],
                    ["Release Time",   fdp.release_time || "—",             "text-foreground"],
                    ["Total FDP",      `${fdp.fdp_hours?.toFixed(1) || 0}h`, "text-indigo-300"],
                    ["DGCA Limit",     `${fdp.dgca_limit || 13}h`,           "text-muted-foreground"],
                  ].map(([l, v, c]) => (
                    <div key={l} className="flex justify-between text-[10px]">
                      <span className="text-muted-foreground">{l}</span>
                      <span className={`font-mono font-bold ${c}`}>{v}</span>
                    </div>
                  ))}
                  <div className="flex justify-between items-center pt-1 border-t border-border/20">
                    <span className="text-[10px] text-muted-foreground">Status</span>
                    <span className="text-[9px] font-bold px-2 py-0.5 rounded-sm font-mono"
                      style={{ color: fdpMeta.color, background: fdpMeta.bg }}>
                      {fdpMeta.label}
                    </span>
                  </div>
                </div>
              </div>
              <div className="border border-border/30 rounded-sm bg-white/[0.02] p-4">
                <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground mb-3">Schedule Summary</div>
                <div className="space-y-2">
                  {[
                    ["Aircraft",        submission.tail_number || "—",       "text-amber-400"],
                    ["Date",            `${sch.month || ""} ${sch.date_day || ""}`, "text-foreground"],
                    ["Effective Date",  deploy.effective_date || "TBD",      "text-teal-400"],
                    ["Total Sectors",   `${totalSectors}`,                   "text-foreground"],
                    ["Total Block Hrs", `${totalBH.toFixed(1)}h`,            "text-indigo-300"],
                  ].map(([l, v, c]) => (
                    <div key={l} className="flex justify-between text-[10px]">
                      <span className="text-muted-foreground">{l}</span>
                      <span className={`font-mono font-bold ${c}`}>{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── Flight schedule ── */}
          <div>
            <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Flight Schedule</div>
            <div className="space-y-1.5">
              {phase1.length > 0 && (
                <div>
                  <div className="text-[8px] text-amber-400/70 font-bold uppercase tracking-widest mb-1">Phase 1 — Scheduled Operations</div>
                  <div className="space-y-1">
                    {phase1.map((s, i) => <SectorRow key={i} sector={s} phase={1} index={i} />)}
                  </div>
                </div>
              )}
              {phase2.length > 0 && (
                <div className="mt-2">
                  <div className="text-[8px] text-indigo-400/70 font-bold uppercase tracking-widest mb-1">Phase 2 — Extra Legs (Scenario)</div>
                  <div className="space-y-1">
                    {phase2.map((s, i) => <SectorRow key={i} sector={s} phase={2} index={i} />)}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Crew assignments ── */}
          <div>
            <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Crew Assignments</div>
            {isDualCrew ? (
              <div className="grid grid-cols-2 gap-3">
                {/* Crew 1 */}
                <div className="border border-amber-500/25 rounded-sm bg-amber-500/5 p-3">
                  <div className="text-[9px] font-bold text-amber-400 uppercase tracking-widest mb-2">
                    Crew 1 — Phase 1 Operations
                  </div>
                  <div className="space-y-1.5">
                    <CrewRow person={ca.crew_1?.captain}       role="Captain" />
                    <CrewRow person={ca.crew_1?.first_officer} role="First Officer" />
                    {(ca.crew_1?.cabin_crew || []).map((cc, i) => (
                      <CrewRow key={i} person={cc} role={cc.role || `CC${i+1}`} />
                    ))}
                  </div>
                  {ca.crew_1?.ai_rationale && (
                    <div className="mt-2 px-2 py-1.5 bg-amber-500/5 border border-amber-500/10 rounded-sm text-[9px] text-amber-300/70 italic">
                      {ca.crew_1.ai_rationale}
                    </div>
                  )}
                </div>
                {/* Crew 2 */}
                <div className="border border-indigo-500/25 rounded-sm bg-indigo-500/5 p-3">
                  <div className="text-[9px] font-bold text-indigo-400 uppercase tracking-widest mb-2">
                    Crew 2 — Phase 2 Extra Legs
                  </div>
                  <div className="space-y-1.5">
                    <CrewRow person={ca.crew_2?.captain}       role="Captain" />
                    <CrewRow person={ca.crew_2?.first_officer} role="First Officer" />
                    {(ca.crew_2?.cabin_crew || []).map((cc, i) => (
                      <CrewRow key={i} person={cc} role={cc.role || `CC${i+1}`} />
                    ))}
                  </div>
                  {ca.crew_2?.ai_rationale && (
                    <div className="mt-2 px-2 py-1.5 bg-indigo-500/5 border border-indigo-500/10 rounded-sm text-[9px] text-indigo-300/70 italic">
                      {ca.crew_2.ai_rationale}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-1.5">
                <CrewRow person={ca.captain}       role="Captain" />
                <CrewRow person={ca.first_officer} role="First Officer" />
                {(ca.cabin_crew || []).map((cc, i) => (
                  <CrewRow key={i} person={cc} role={cc.role || `CC${i + 1}`} />
                ))}
                {ca.ai_rationale && (
                  <div className="mt-2 px-3 py-2 bg-teal-500/5 border border-teal-500/15 rounded-sm text-[10px] text-teal-300/80 leading-relaxed">
                    <span className="font-bold text-teal-400">AI Rationale:</span> {ca.ai_rationale}
                  </div>
                )}
              </div>
            )}
            {ca.dgca_compliance_note && (
              <div className="mt-2 text-[9px] text-emerald-300/70 italic px-2">{ca.dgca_compliance_note}</div>
            )}
          </div>

          {/* ── Approval comments ── */}
          {approval.comments && (
            <div className="px-4 py-3 bg-white/[0.03] border border-border/30 rounded-sm">
              <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground mb-1">Ops Manager Comments</div>
              <div className="text-[11px] text-foreground italic">"{approval.comments}"</div>
              <div className="text-[9px] text-muted-foreground mt-1">— {approval.approved_by}</div>
            </div>
          )}

          {/* ── Management sign-off block ── */}
          <div className="border border-indigo-500/20 bg-indigo-500/5 rounded-sm px-5 py-4 space-y-4">
            <div className="text-[9px] font-bold uppercase tracking-widest text-indigo-400">Management Sign-off</div>
            <div className="grid grid-cols-3 gap-4">
              {[
                "Director of Flight Operations",
                "Head of Crew Planning",
                "VP Operations",
              ].map(role => (
                <div key={role} className="space-y-2">
                  <div className="text-[9px] text-muted-foreground">{role}</div>
                  <div className="h-px bg-border/40" />
                  <div className="h-6 border-b border-dashed border-border/30" />
                  <div className="text-[8px] text-muted-foreground/50">Signature / Date</div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Footer ── */}
          <div className="text-center text-[8px] text-muted-foreground/40 border-t border-border/20 pt-3">
            Fatigue Shield · Airline Operations · Generated {new Date().toLocaleString("en-IN")} IST
            {" "} | Confidential — Crew Operations Use Only
          </div>
        </div>
      </div>
    </div>
  );
}
